<script lang="ts" setup>
import { ref, onMounted, reactive } from "vue";
import {
  getKennungValueDisplay,
  getKZValue,
  getKennungValue,
  getKennungTitle,
} from "@/DataHelper";
import { checkBegriff } from "@/local";
import { getSiteData, goBack } from "@/NavigationHelper";
import AUApi from "@/api";
import { mountedBase, onSave, BaseSetup } from "@/DataLoaderStatic";

import {
  createEditorValidator,
  provideEditorValidator,
} from "@/components/EditorValidator";
import { showSnackbar } from "@/UIHelper";
const validator = createEditorValidator();
provideEditorValidator(validator);

const emit = defineEmits(["back", "save", "delete"]);

let ort = ref<string>("");
let selectFahrzeug = ref<boolean>(false);
let showAbfahrt = ref<boolean>(false);
let showZeit = ref<boolean>(false);
let fahrtkosten: boolean = false;
let editTransport = ref<boolean>(false);
let addresseAbfahrt = ref<any>();
let addresseAnkunft = ref<any>();
let abfahrtKoordinaten = ref<string>("");
let ankunftsKoordinaten = ref<string>("");
let showKoordinaten = ref<boolean>(false);

const pageParams: DataPageParameter = {
  backOnSave: true,
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "Web_RK_EditReisezeiten",
  emitBack: () => handleBack(false),
};

const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));

onMounted(async () => {
  const siteData = getSiteData();
  if (siteData?.showZeit) {
    showZeit = siteData?.showZeit;
  }
  await mountedBase(uiData, pageParams, false);
  if (!uiData.showLoading && uiData.data) {
    const row = uiData.data[0];
    selectFahrzeug.value = getKennungValueDisplay(
      row,
      "RK_TRANSPORTMITTEL",
      uiData.columns
    );
    showKoordinaten.value = getKennungTitle("RK_ABFAHRTSKOORIDNATEN", uiData.columns);
    addresseAbfahrt.value = getKennungValue(
      row,
      "RK_ABFAHRTSKOORIDNATEN",
      uiData.columns
    );
    addresseAnkunft.value = getKennungValue(
      row,
      "RK_ANKUNFTSKOORIDNATEN",
      uiData.columns
    );
    fahrtkosten = getKennungValue(row, "Fahrtkosten", uiData.columns);
    if (!showKoordinaten.value) {
      ort.value = getKennungValueDisplay(row, "CITY", uiData.columns);
    }
    showAbfahrt.value = getKZValue(row, "showAbfahrt", uiData.columns);
    // bei Abfahrt darf das Transportmittel IMMER verwändert werden
    editTransport.value = getKZValue(row, "showTransportmittel", uiData.columns);
    if (showAbfahrt.value) {
      fahrtkosten = true;
      editTransport.value = true;
    }
  }
  uiData.showLoading = false;
  uiData.initialized = true;
});

function checkValue(evt: any) {
  fahrtkosten = evt.value.setShow;
}
async function onValidate() {
  return await validator.validateAsync();
}
async function save() {
  const isvalid = await onValidate();
  if (!isvalid.valid) {
    showSnackbar({
      text:
        "Validierung fehlgeschlagen - Bitte prüfen! (" +
        isvalid.invalidFields.join(", ") +
        ")",
      duration: 4000,
      color: "error",
    });
    return;
  }
  const success = await onSave(
    uiData,
    pageParams,
    0,
    "",
    uiData.zeitbereich,
    pageParams.aic,
    true,
    pageParams.varUebergabe
  );
  if (success) {
    emit("save");
  }
}
function handleBack(saved: boolean = false) {
  console.log("🔙 DialogFreigabe - handleBack aufgerufen, saved:", saved);
  emit("back", saved);
}
function setValueAnkunft(res: any) {
  addresseAnkunft.value = res;
}
function setValueAbfahrt(res: any) {
  addresseAbfahrt.value = res;
}
</script>
<template>
  <v-card class="pa-2" :loading="uiData.showLoading" v-if="uiData.initialized">
    <v-row class="py-2 pl-3 ma-0">
      <span style="white-space: nowrap"
        >{{ getKennungTitle("RK_REISEEREIGNISSE", uiData.columns) }}:
      </span>

      <span class="pl-2" style="white-space: nowrap">{{
        getKennungValueDisplay(uiData.data[0], "RK_REISEEREIGNISSE", uiData.columns)
      }}</span>
    </v-row>
    <v-row v-if="selectFahrzeug && !editTransport" class="pb-6 pl-6">
      <span style="white-space: nowrap"
        >{{ getKennungTitle("RK_TRANSPORTMITTEL", uiData.columns) }}:
      </span>

      <span class="pl-2" style="white-space: nowrap">{{
        getKennungValueDisplay(uiData.data[0], "RK_TRANSPORTMITTEL", uiData.columns)
      }}</span>
    </v-row>
    <EditTimePicker kennung="RK_ABFAHRTSZEIT" v-if="showZeit" />
    <EditComboBox
      v-if="showAbfahrt && !showKoordinaten"
      kennung="RK_ABFAHRTSORT"
      :queryZeitbereich="pageParams.zeitbereich"
      :loadFavorite="true"
      :clearable="true"
    />
    <EditGps
      v-if="showAbfahrt && showKoordinaten"
      v-model="abfahrtKoordinaten"
      kennung="RK_ABFAHRTSKOORIDNATEN"
      :queryZeitbereich="pageParams.zeitbereich"
      :showMap="true"
      @change="setValueAbfahrt"
    />
    <EditComboBox
      v-if="!showKoordinaten"
      v-model="ort"
      kennung="CITY"
      :queryZeitbereich="pageParams.zeitbereich"
      :loadFavorite="true"
      :clearable="true"
    />
    <EditGps
      v-if="showKoordinaten"
      v-model="ankunftsKoordinaten"
      kennung="RK_ANKUNFTSKOORIDNATEN"
      :queryZeitbereich="pageParams.zeitbereich"
      :loadFavorite="true"
      :clearable="false"
      :showMap="true"
      @change="setValueAnkunft"
    />
    <EditTextArea kennung="BEMERKUNG" />
    <EditToggleSight
      v-if="editTransport && checkBegriff('Web_RK_Fahrzeugwechsel')"
      kennung="RK_TRANSPORTMITTEL"
      :queryZeitbereich="pageParams.zeitbereich"
      v-model="selectFahrzeug"
      @seteditvalue="checkValue"
    />
    <v-card-actions>
      <v-spacer></v-spacer>
      <CommonBaseButtons
        :deleteVisible="false"
        :createVisible="false"
        :refreshVisible="false"
        :backVisible="false"
        :saveVisible="true"
        @back="handleBack(false)"
        @save="save"
      ></CommonBaseButtons>
    </v-card-actions>
  </v-card>
</template>
<style scoped>
.row {
  padding: 0px;
}
.v-stepper__step {
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -ms-flex-direction: row;
  flex-direction: row;
  padding-left: 5px;
  padding-right: 0px;
  padding-top: 10px;
  padding-bottom: 10px;
  position: relative;
}
.theme--light.v-stepper .v-stepper__step--active .v-stepper__label {
  text-shadow: 0px 0px 0px black;
}
</style>
